import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import { t } from "i18next";
import cx from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { Badge } from "../../common";
import ToDoListTaskTypeDto from "../../../types/dtos/dashboards/right-bar/to-do-list/ToDoListTaskTypeDto";
import { useSlidingTabIndicator } from "../../../hooks/useSlidingTabIndicator";

interface TaskTypeSelectorProps {
  toDoListTaskTypes: ToDoListTaskTypeDto[];
  selectedTaskTypeId: string | number;
  showCount: boolean;
  tabPanelId?: string;
  onSelectedTaskTypeIdChange(
    standardTaskTypeId?: number | null,
    advancedTaskTypeId?: string | null,
  ): void;
}

/** How many equal-height tile buttons are shown at once in carousel mode (4+ task types). */
const CAROUSEL_VISIBLE_COUNT = 3;

const TAB_LIST_CLASS =
  "task-type-tabs rounded-lg border border-gray-200 bg-gray-100/60 p-0.5";

const INLINE_TAB_LIST_CLASS = "inline-flex items-stretch gap-0.5";

function isTaskTypeSelected(
  taskType: ToDoListTaskTypeDto,
  selectedTaskTypeId: string | number,
) {
  return (
    selectedTaskTypeId === taskType.standardTaskTypeId ||
    selectedTaskTypeId === taskType.advancedTaskTypeId
  );
}

function getTaskTypeTabId(taskType: ToDoListTaskTypeDto): string {
  const id = taskType.standardTaskTypeId ?? taskType.advancedTaskTypeId;
  return `task-type-tab-${id}`;
}

function getSelectedTaskTypeIndex(
  taskTypes: ToDoListTaskTypeDto[],
  selectedTaskTypeId: string | number,
): number {
  return taskTypes.findIndex((taskType) =>
    isTaskTypeSelected(taskType, selectedTaskTypeId),
  );
}

interface TaskTypeTabListProps {
  ariaLabel: string;
  className?: string;
  selectedTaskTypeId: string | number;
  selectedTabRef: RefObject<HTMLButtonElement>;
  layoutKey?: string | number;
  children: ReactNode;
}

/** Tablist track with a sliding pill behind the selected tab. */
function TaskTypeTabList({
  ariaLabel,
  className,
  selectedTaskTypeId,
  selectedTabRef,
  layoutKey,
  children,
}: TaskTypeTabListProps) {
  const tabListRef = useRef<HTMLDivElement>(null);
  const pillRef = useSlidingTabIndicator({
    tabListRef,
    selectedTabRef,
    selectedId: selectedTaskTypeId,
    layoutKey,
  });

  return (
    <div
      ref={tabListRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cx(TAB_LIST_CLASS, className)}
    >
      <span
        ref={pillRef}
        className="task-type-tabs-pill"
        aria-hidden="true"
      />
      {children}
    </div>
  );
}

/**
 * Task-type filter for the dashboard to-do list (and admin user-detail task section).
 *
 * Layout is chosen from the number of task types:
 * - 1–2: inline tabs (label and count badge on one line)
 * - 3: equal-height grid tiles (label above count, three columns)
 * - 4+: same tiles as above, with prev/next carousel showing three at a time
 */
function TaskTypeSelector({
  toDoListTaskTypes,
  selectedTaskTypeId,
  showCount,
  tabPanelId,
  onSelectedTaskTypeIdChange,
}: TaskTypeSelectorProps) {
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const selectedTabRef = useRef<HTMLButtonElement>(null);
  const taskTypeCount = toDoListTaskTypes.length;
  const useCarousel = taskTypeCount > CAROUSEL_VISIBLE_COUNT;
  const maxCarouselStartIndex = Math.max(
    0,
    taskTypeCount - CAROUSEL_VISIBLE_COUNT,
  );

  useEffect(() => {
    setCarouselStartIndex((prev) => Math.min(prev, maxCarouselStartIndex));
  }, [maxCarouselStartIndex]);

  // Keep the selected tab visible in carousel mode.
  useEffect(() => {
    if (!useCarousel) {
      return;
    }

    const selectedIndex = getSelectedTaskTypeIndex(
      toDoListTaskTypes,
      selectedTaskTypeId,
    );
    if (selectedIndex < 0) {
      return;
    }

    setCarouselStartIndex((prev) => {
      if (
        selectedIndex >= prev &&
        selectedIndex < prev + CAROUSEL_VISIBLE_COUNT
      ) {
        return prev;
      }

      const alignedStart = Math.min(
        selectedIndex,
        Math.max(0, selectedIndex - CAROUSEL_VISIBLE_COUNT + 1),
      );
      return Math.min(alignedStart, maxCarouselStartIndex);
    });
  }, [
    selectedTaskTypeId,
    toDoListTaskTypes,
    useCarousel,
    maxCarouselStartIndex,
  ]);

  const tabListLabel = t("DashboardRightBar.TaskTypeFilter", "Task type");

  /** Renders one task-type tab. `inline` = horizontal label + badge; `tile` = stacked, equal-height cell. */
  const renderTaskTypeTab = (
    taskType: ToDoListTaskTypeDto,
    layout: "inline" | "tile",
  ) => {
    const isSelected = isTaskTypeSelected(taskType, selectedTaskTypeId);
    const label = t(taskType.pluralTranslationKeyIdentifier);
    const tabId = getTaskTypeTabId(taskType);

    return (
      <button
        key={taskType.pluralTranslationKeyIdentifier}
        ref={isSelected ? selectedTabRef : undefined}
        type="button"
        role="tab"
        id={tabId}
        aria-selected={isSelected}
        aria-controls={tabPanelId}
        onClick={() =>
          onSelectedTaskTypeIdChange(
            taskType.standardTaskTypeId,
            taskType.advancedTaskTypeId,
          )
        }
        className={cx(
          "task-type-tab rounded-md text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
          isSelected
            ? "text-gray-900"
            : "text-gray-600 hover:text-gray-900 hover:cursor-pointer",
          layout === "inline" &&
            "inline-flex h-full min-h-9 items-center gap-1.5 whitespace-nowrap px-3",
          layout === "tile" &&
            "flex h-full w-full flex-col items-center justify-center gap-1 px-2 py-3 min-h-[4.5rem] text-center leading-tight",
        )}
      >
        {layout === "inline" ? (
          <>
            {label}
            {showCount && (
              <Badge
                text={taskType.count.toString()}
                backgroundColourClassName="accent-bg"
                textColourClassName="text-white"
                marginClassName=""
                classNames={
                  isSelected ? "hover:cursor-default" : "hover:cursor-pointer"
                }
              />
            )}
          </>
        ) : (
          <>
            <span>{label}</span>
            {showCount && (
              <Badge
                text={taskType.count.toString()}
                backgroundColourClassName="accent-bg"
                textColourClassName="text-white"
                marginClassName=""
                classNames={
                  isSelected ? "hover:cursor-default" : "hover:cursor-pointer"
                }
              />
            )}
          </>
        )}
      </button>
    );
  };

  // 1–2 task types: inline tab row.
  if (taskTypeCount < 3) {
    return (
      <TaskTypeTabList
        ariaLabel={tabListLabel}
        selectedTaskTypeId={selectedTaskTypeId}
        selectedTabRef={selectedTabRef}
        className={INLINE_TAB_LIST_CLASS}
      >
        {toDoListTaskTypes.map((taskType) =>
          renderTaskTypeTab(taskType, "inline"),
        )}
      </TaskTypeTabList>
    );
  }

  // Exactly 3 task types: full-width equal-height tile grid (no carousel).
  if (!useCarousel) {
    return (
      <TaskTypeTabList
        ariaLabel={tabListLabel}
        selectedTaskTypeId={selectedTaskTypeId}
        selectedTabRef={selectedTabRef}
        className="grid grid-cols-3 gap-0.5 items-stretch"
      >
        {toDoListTaskTypes.map((taskType) =>
          renderTaskTypeTab(taskType, "tile"),
        )}
      </TaskTypeTabList>
    );
  }

  // 4+ task types: tile carousel — three visible, shift one at a time.
  const visibleTaskTypes = toDoListTaskTypes.slice(
    carouselStartIndex,
    carouselStartIndex + CAROUSEL_VISIBLE_COUNT,
  );
  const rangeStart = carouselStartIndex + 1;
  const rangeEnd = Math.min(
    carouselStartIndex + CAROUSEL_VISIBLE_COUNT,
    taskTypeCount,
  );

  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs text-gray-500 text-center" aria-live="polite">
        {t(
          "DashboardRightBar.ShowingTasksRange",
          "Showing {{start}}–{{end}} of {{total}}",
          {
            start: rangeStart,
            end: rangeEnd,
            total: taskTypeCount,
          },
        )}
      </p>
      <div className="flex items-stretch gap-1">
        <button
          type="button"
          aria-label={t(
            "DashboardRightBar.ShowPreviousTaskTypes",
            "Show previous task types",
          )}
          disabled={carouselStartIndex === 0}
          onClick={() =>
            setCarouselStartIndex((prev) => Math.max(0, prev - 1))
          }
          className="flex-shrink-0 self-center p-1 text-gray-500 disabled:opacity-30 disabled:cursor-default hover:cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        >
          <FontAwesomeIcon icon={faChevronLeft} size="sm" aria-hidden="true" />
        </button>
        <TaskTypeTabList
          ariaLabel={tabListLabel}
          selectedTaskTypeId={selectedTaskTypeId}
          selectedTabRef={selectedTabRef}
          layoutKey={carouselStartIndex}
          className="flex flex-1 min-w-0 gap-0.5 items-stretch"
        >
          {visibleTaskTypes.map((taskType) =>
            renderTaskTypeTab(taskType, "tile"),
          )}
        </TaskTypeTabList>
        <button
          type="button"
          aria-label={t(
            "DashboardRightBar.ShowNextTaskTypes",
            "Show next task types",
          )}
          disabled={carouselStartIndex >= maxCarouselStartIndex}
          onClick={() =>
            setCarouselStartIndex((prev) =>
              Math.min(maxCarouselStartIndex, prev + 1),
            )
          }
          className="flex-shrink-0 self-center p-1 text-gray-500 disabled:opacity-30 disabled:cursor-default hover:cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        >
          <FontAwesomeIcon icon={faChevronRight} size="sm" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default TaskTypeSelector;
